import { Select } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { useModalContext } from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useSingleData from '@/hooks/pure/useSingleData';
import service from '@/services/index';
import useLocaleStore from '@/stores/LocaleStore';
import { modalStyle } from '@/styles/style';

import type { GooglePlayAppInfoT, GooglePlayStoreInfo } from '../types/types';

const GooglePlayStoreConnectModal = (props: {
  onConnectComplete: (info: GooglePlayStoreInfo) => void;
}) => {
  const regionMap = useLocaleStore((state) => state.regionMap);
  const [currCountry, setCurrCountry] = useState<{
    country: string;
    lang: string;
  } | null>(null);
  const [currApp, setCurrApp] = useState<GooglePlayAppInfoT | null>(null);
  const { closeModal } = useModalContext();
  const [appSearch, setAppSearch] = useState('');

  const fetchAppData = useCallback(async () => {
    if (!appSearch || !currCountry) return [];
    const appInfos = await service.googlePlay.searchApp(
      appSearch,
      currCountry.country,
      currCountry.lang,
    );
    return appInfos;
  }, [appSearch, currCountry]);

  const { data: appList, loading: appLoading } =
    useSingleData<GooglePlayAppInfoT[]>(fetchAppData);

  const appOptions = useMemo(() => {
    if (!appList) return [];
    return appList?.map((a) => {
      return {
        value: a.app_id,
        label: (
          <div className="flex items-center gap-2">
            <img
              src={a.icon}
              alt={a.app_id}
              width="20px"
              height="20px"
              style={{ verticalAlign: 'middle' }}
            />
            &nbsp;&nbsp;
            {a.title}
          </div>
        ),
        searchValue: a.title,
      };
    });
  }, [appList]);

  const debouncedSetAppSearch = useDebouncedCallback(
    (search: string) => setAppSearch(search),
    500,
  );

  const onAddClick = async () => {
    if (!currApp || !currCountry) return;
    const appInfo = {
      ...currApp,
      country: currCountry.country,
      lang: currCountry.lang,
    };
    const data = await service.googlePlay.createGooglePlayStoreInfo(appInfo);

    props.onConnectComplete(data);
    closeModal();
  };

  return (
    <ModalStyle>
      <Typo type="label0" weight="sb">
        Add new apps
      </Typo>
      <Space h={20} />
      <Typo type="body3" className="text-gray-700">
        Syncly will ingest the most <strong>recent 500 reviews</strong> from the
        Google Play Store.
        <br />
        New reviews will be ingested to Syncly <strong>every hour.</strong>
      </Typo>
      <Space h={40} />
      <Typo type="label2" className="text-gray-700">
        Country
      </Typo>
      <Space h={8} />
      <Select
        showSearch
        size="large"
        optionFilterProp="searchValue"
        placeholder="Select or search country"
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode as HTMLDivElement;
        }}
        onSelect={(v: string) =>
          setCurrCountry({
            country: v.toLowerCase(),
            lang: regionMap[v].lang,
          })
        }
        options={Object.entries(regionMap).map(([key, value]) => {
          return {
            value: key,
            label: value.country,
            searchValue: value.country,
          };
        })}
      />
      <Space h={32} />
      <Typo type="label2" className="text-gray-700">
        App
      </Typo>
      <Space h={8} />
      <Select
        showSearch
        loading={appLoading}
        onSelect={(v) => {
          const currApp = appList?.find((a) => a.app_id === v);
          setCurrApp(currApp || null);
        }}
        onSearch={debouncedSetAppSearch}
        placeholder="Search app"
        optionLabelProp="label"
        filterOption={false}
        size="large"
        disabled={!currCountry}
        getPopupContainer={() =>
          document.getElementById('dropdown-layer') || document.body
        }
        options={appOptions}
      />

      <Space h={40} />
      <Flex j="flex-end" g={12}>
        <Button
          variant="outlined"
          sizeType="m"
          style={{ width: 120 }}
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          sizeType="m"
          style={{ width: 120 }}
          onClick={onAddClick}
          disabled={!currApp}
        >
          Add
        </Button>
      </Flex>
    </ModalStyle>
  );
};

const ModalStyle = styled(VFlex)`
  width: 620px;
  padding: 32px;
  ${modalStyle};
`;

export default GooglePlayStoreConnectModal;
